'use strict';

/**
 * Base class for a Collection supporting filter, search and sort
 *
 * @category   Collection
 * @module     base
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Oliver Spindler <os@oliverspindler.com>
 */

var Backbone = require('backbone');
var Model = require('base/Colido.Model');
var FilteredCollection = require('base/Colido.FilteredCollection');

var ParameterizedCollection = FilteredCollection.extend({
    initialize: function(models, options) {
        options = _.extend({}, options);
        this.options = options;

        this.params = options.params || new Model();
        this.collectionFilter = this.collectionFilter.bind(this);
        this.setFilterFunction(this.params.get('filter'));

        this.onChangeSort(this.params, this.params.get('sort'));
        this.onChangeSearch(this.params, this.params.get('search'));
        this.onChangeSetModel(this.params, this.params.get('setModel'));
        this.onChangeLocationModel(this.params, this.params.get('locationModel'));
        this.onChangeImportModel(this.params, this.params.get('importModel'));
        this.onChangeImportWarningsModel(this.params, this.params.get('importWarningsModel'));

        FilteredCollection.prototype.initialize.apply(this, arguments);

        this.catalog = this.collection.catalog;
        this.comparator = this.collection.comparator;

        this.listenTo(this.params, 'change', this.onChange);
        this.listenTo(this.params, 'change:activeModel', this.onChangeActiveModel);
        this.listenTo(this.params, 'change:sort', this.onChangeSort);
        this.listenTo(this.params, 'change:controlMode', this.onChangeControlMode);
        this.listenTo(this.collection, 'sort', this.sort);
        this.sort();
    },

    resetCollection: function () {
        this._mapping = [];
        this._reset();
        this.setFilter(undefined, {silent: true});
        this.sort({silent: true});
        this.trigger('reset', this);
    },

    observedAttrs: ['search', 'filter', 'setModel', 'locationModel', 'importModel', 'importWarningsModel'],

    onChange: function (params) {
        var changed = params.changed;

        var needsReset = false;
        _.each(this.observedAttrs, function (attr) {
            if (_.isUndefined(changed[attr])) { return; }

            var method = 'onChange' + _.ucfirst(attr);
            this[method](params, changed[attr]);
            needsReset = true;
        }.bind(this));

        if (needsReset) {
            this.resetCollection();
        }
    },

    onChangeSearch: function(params, searchString) {
        var terms = _.isString(searchString) && searchString.toLowerCase().split(',');
        if (terms.length === 1 && !terms[0]) {
            terms = [];
        }
        this.searchTerms = terms;
    },

    onChangeFilter: function(params, filterString) {
        this.setFilterFunction(filterString);
    },

    onChangeSetModel: function(params, setModel) {
        if (params.previous('setModel') instanceof Backbone.Model) {
            this.stopListening(params.previous('setModel'));
        }
        this.setModel = params.get('setModel');
        if (this.setModel instanceof Backbone.Model) {
            this.listenTo(this.setModel, 'change:items', this.onChangeSetItems)
        }
    },

    onChangeLocationModel: function(params, locationModel) {
        if (params.previous('locationModel') instanceof Backbone.Model) {
            this.stopListening(params.previous('locationModel'));
        }
        this.locationModel = params.get('locationModel');
        if (this.locationModel instanceof Backbone.Model) {
            this.listenTo(this.locationModel, 'change:items', this.onChangeLocationItems)
            this.listenTo(this.locationModel, 'change:itemsRecursive', this.onChangeLocationItems)
        }
    },

    onChangeImportModel: function(params, importModel) {
        if (params.previous('importModel') instanceof Backbone.Model) {
            this.stopListening(params.previous('importModel'));
        }

        this.importModel = params.get('importModel');
    },

    onChangeImportWarningsModel: function(params, importWarningsModel) {
        if (params.previous('importWarningsModel') instanceof Backbone.Model) {
            this.stopListening(params.previous('importWarningsModel'));
        }

        this.importWarningsModel = params.get('importWarningsModel');
    },

    onChangeSort: function(params, sortString) {
        this.sortByFields(sortString);
    },

    onChangeSetItems: function (setModel, items) {
        this.resetCollection();
    },

    onChangeLocationItems: function (locationModel, items) {
        this.resetCollection();
    },

    setFilterFunction: function(filterString) {
        var collection = this.collection || this.options.collection;
        this._filterFunction = collection._parseFilterString(this.params.get('filter'));
    },

    collectionFilter: function(model, index) {
        if (!this.params) {
            return true;
        }

        if (!this._matchesSearch(model, index)) {
            return false;
        }

        if (!this._matchesSetModel(model, index)) {
            return false;
        }

        if (!this._matchesLocationModel(model, index)) {
            return false;
        }

        if (!this._matchesImportModel(model, index)) {
            return false;
        }

        if (!this._matchesImportWarningsModel(model, index)) {
            return false;
        }

        if (this._filterFunction && !this._filterFunction(model, index)) {
            return false;
        }

        return true;
    },

    _matchesSearch: function (model, index) {
        var isItemMatching = _.every(this.searchTerms, function(term){
            return _.any(this.collection.searchFields, function(field) {
                var value = model.get(field);
                value = _.isArray(value) ? value.join(',') : value;

                if (_.isNumber(value)) {
                    return parseFloat(value) === parseFloat(term);
                }

                if (_.isString(value)) {
                    return value.toLowerCase().indexOf(term) > -1;
                }

                return false;
            })
        }.bind(this));

        return isItemMatching;
    },

    _matchesSetModel: function (model, index) {
        if (!(this.setModel instanceof Backbone.Model)) {
            return true;
        }

        return _.contains(this.setModel.get('items'), model.id);
    },

    _matchesLocationModel: function (model, index) {
        if (!(this.locationModel instanceof Backbone.Model)) {
            return true;
        }

        return _.contains(this.locationModel.get('itemsRecursive'), model.id) ||
               _.contains(this.locationModel.get('items'), model.id);
    },

    _matchesImportModel: function (model, index) {
        if (!(this.importModel instanceof Backbone.Model)) {
            return true;
        }

        return _.contains(this.importModel.get('importedItems'), model.id);
    },

    _matchesImportWarningsModel: function (model, index) {
        if (!(this.importWarningsModel instanceof Backbone.Model)) {
            return true;
        }

        return false; // We don't have warning models yes
    },

    getFilteredCount: function (filter) {
        if (filter) {
            var filterFunction = this.collection._buildFilterFunction(filter);

            return this.collection.filter.call(this, filterFunction).length;
        } else {
            return this.length;
        }
    },

    activate: function (model) {
        if (model != null && !(model instanceof Backbone.Model)) {
            model = this.get(model);
        }

        this.params.set('activeModel', model);
    },

    activateIndex: function (index) {
        this.activate(this.at(index));
    },

    activateNext: function () {
        var index = this.indexOf(this.params.get('activeModel'));
        index = index === -1 ? 0 : (index + 1) % this.length;
        this.params.set('activeModel', this.at(index));
    },

    activatePrevious: function () {
        var index = this.indexOf(this.params.get('activeModel'));
        if (index === -1 || index === 0) {
            index = this.length - 1;
        } else {
            index--;
        }
        this.params.set('activeModel', this.at(index));
    },

    onChangeActiveModel: function (params, activeModel) {
        var previousActiveModel = params.previous('activeModel');
        previousActiveModel && previousActiveModel.set('active', null);
        activeModel && activeModel.set('active', true);
    },

    onChangeControlMode: function (params, controlMode) {
        if (controlMode === 'default') {
            this.collection.deselectAll();
        }
    },
});

module.exports = ParameterizedCollection;